import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_LARGE,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { toSnakeCase } from '@vestigas/utils';

import { SITE_DETAILS_SORT_BY } from './constants';

/**
 * Zod schema for validating sites details query parameters.
 */
export const zodSitesDetailsQueryParamsSchema = z
  .object({
    dateRange: z.object({
      dateFrom: z.iso.date(),
      dateTo: z.iso.date(),
    }),
    limit: z
      .number()
      .int()
      .min(1)
      .max(500)
      .prefault(PAGINATION_PAGE_SIZE_LARGE),
    offset: z.number().int().nonnegative().prefault(0),
    page: z.number().int().nonnegative().optional(),
    searchString: z.string().trim().optional().prefault(''),
    sortBy: z
      .preprocess(
        (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
        z.enum(SITE_DETAILS_SORT_BY),
      )
      .optional()
      .prefault('site_name')
      .catch('site_name'),
    sortOrder: z
      .preprocess(
        (value) => String(value).toUpperCase(),
        z.enum(PAGINATION_SORT_DIRECTION),
      )
      .optional()
      .prefault('DESC'),
  })
  .transform(({ limit, offset, page, ...rest }) => {
    return {
      ...rest,
      limit,
      offset: page ? page * limit : offset,
    };
  });

/**
 * Zod schema for validating a single site details table item.
 */
const zodSiteDetailsItemSchema = z.object({
  assignedDeliveryNotesCount: z.number().int().nonnegative(),
  isActive: z.boolean(),
  signatureRate: z.number().nonnegative(),
  signedDeliveryNotesCount: z.number().int().nonnegative(),
  siteId: z.uuid(),
  siteName: z.string(),
  unsignedDeliveryNotesCount: z.number().int().nonnegative(),
});

/**
 * Zod schema for validating the sites details API response.
 */
export const zodSitesDetailsResponseSchema = z.object({
  data: z.array(zodSiteDetailsItemSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number().int().nonnegative(),
});
